import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { User } from '@supabase/supabase-js';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CalendarIcon, RefreshCw, Filter } from 'lucide-react';
import { TradesHistoryTab } from './TradesHistoryTab';
import { SignalProcessingTab } from './SignalProcessingTab';
import { EndpointManagement } from './EndpointManagement';
import { LiveMonitorTab } from './LiveMonitorTab';
import { toast } from 'sonner';

interface Bot {
  id: string;
  name: string;
  signal_token: string;
}

export function TrackingBotSignal() {
  const [user, setUser] = useState<User | null>(null);
  const [bots, setBots] = useState<Bot[]>([]);
  const [selectedBotId, setSelectedBotId] = useState<string>('all');
  const [selectedInstrument, setSelectedInstrument] = useState<string>('all');
  const [dateRange, setDateRange] = useState({
    from: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    to: new Date().toISOString().split('T')[0]
  });
  const [isLoading, setIsLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  // Get current user
  useEffect(() => {
    const getUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setUser(user);
    };
    getUser();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Fetch available bots
  useEffect(() => {
    const fetchBots = async () => {
      if (!user) return;

      const { data, error } = await supabase
        .from('bots')
        .select('id, name, signal_token')
        .order('name');

      if (error) {
        console.error('Error fetching bots:', error);
        toast.error('Failed to load bots');
        return;
      }

      setBots(data || []);
    };

    fetchBots();
  }, [user]);

  // Real-time subscription for new events
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel('bot-signal-events')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'bot_signal_events'
        },
        (payload) => {
          console.log('New signal event:', payload);
          // Trigger refresh of data
          setRefreshKey(prev => prev + 1);
          toast.success('New signal received');
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  const handleRefresh = () => {
    setRefreshKey(prev => prev + 1);
    toast.success('Data refreshed');
  };

  const filters = {
    botId: selectedBotId !== 'all' ? selectedBotId : null,
    instrument: selectedInstrument !== 'all' ? selectedInstrument : null,
    dateFrom: dateRange.from,
    dateTo: dateRange.to,
  };

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Card>
          <CardHeader>
            <CardTitle>Authentication Required</CardTitle>
            <CardDescription>Please log in to access bot signal tracking</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Bot Signal Tracking</h1>
        <p className="text-muted-foreground">
          Monitor and analyze bot signals from TradingView webhooks
        </p>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Bot Selection */}
            <div className="space-y-2">
              <Label htmlFor="bot-select">Bot</Label>
              <Select value={selectedBotId} onValueChange={setSelectedBotId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select bot" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Bots</SelectItem>
                  {bots.map((bot) => (
                    <SelectItem key={bot.id} value={bot.id}>
                      {bot.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Instrument Filter */}
            <div className="space-y-2">
              <Label htmlFor="instrument-select">Instrument</Label>
              <Select value={selectedInstrument} onValueChange={setSelectedInstrument}>
                <SelectTrigger>
                  <SelectValue placeholder="Select instrument" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Instruments</SelectItem>
                  <SelectItem value="ETHUSD.P">ETHUSD.P</SelectItem>
                  <SelectItem value="BTCUSD">BTCUSD</SelectItem>
                  <SelectItem value="XAUUSD">XAUUSD</SelectItem>
                  {/* Add more instruments as needed */}
                </SelectContent>
              </Select>
            </div>

            {/* Date From */}
            <div className="space-y-2">
              <Label htmlFor="date-from">From Date</Label>
              <Input
                id="date-from"
                type="date"
                value={dateRange.from}
                onChange={(e) => setDateRange(prev => ({ ...prev, from: e.target.value }))}
              />
            </div>

            {/* Date To */}
            <div className="space-y-2">
              <Label htmlFor="date-to">To Date</Label>
              <Input
                id="date-to"
                type="date"
                value={dateRange.to}
                onChange={(e) => setDateRange(prev => ({ ...prev, to: e.target.value }))}
              />
            </div>
          </div>

          <div className="flex justify-end mt-4">
            <Button onClick={handleRefresh} variant="outline" size="sm">
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Main Tabs */}
      <Tabs defaultValue="live-monitor" className="space-y-6">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="live-monitor">Live Monitor</TabsTrigger>
          <TabsTrigger value="trades-history">Trades History</TabsTrigger>
          <TabsTrigger value="signal-processing">Signal Processing</TabsTrigger>
          <TabsTrigger value="endpoints">Endpoint Management</TabsTrigger>
        </TabsList>

        <TabsContent value="live-monitor">
          <LiveMonitorTab 
            filters={filters} 
            refreshKey={refreshKey}
            bots={bots}
          />
        </TabsContent>

        <TabsContent value="trades-history">
          <TradesHistoryTab 
            filters={filters} 
            refreshKey={refreshKey}
            bots={bots}
          />
        </TabsContent>

        <TabsContent value="signal-processing">
          <SignalProcessingTab 
            filters={filters} 
            refreshKey={refreshKey}
            bots={bots}
          />
        </TabsContent>

        <TabsContent value="endpoints">
            <EndpointManagement 
              filters={filters}
              refreshKey={refreshKey}
            />
        </TabsContent>
      </Tabs>
    </div>
  );
}